import { Key } from "@zapier/design-system";
import { useMemo, useState } from "react";
import uniqBy from "lodash.uniqby";
import { useHydratePendingConnectionShares } from "./useHydratePendingConnectionShares";
import { ConnectionShare } from "./useFormatConnectionShares";
import {
  addPendingShare,
  findShare,
  noPendingShares,
  PendingShares,
  removePendingShare,
} from "./pendingConnectionShares";
import { ConnectionShareType } from "../../types";

const shareKey = ({ id, shareType }: { id: Key; shareType: string }) =>
  `${shareType}:${id}`;

export default function usePendingConnectionSharesState(
  shares: ConnectionShare[],
) {
  const [pending, setPending] = useState<PendingShares>(noPendingShares);
  const { toDelete } = pending;

  const { hydrated: toAdd, isLoading } = useHydratePendingConnectionShares(
    pending.toAdd,
  );

  const onAdd = (id: Key, shareType: ConnectionShareType) =>
    setPending((previous) => addPendingShare(previous, shares, id, shareType));

  const onRemove = (id: Key, shareType: ConnectionShareType) =>
    setPending((previous) =>
      removePendingShare(previous, shares, id, shareType),
    );

  // Takes the list of current shares, adds pending shares, and removes pending deletes
  const sharesToDisplay = useMemo(() => {
    const loadedShares = [...shares];
    // Add all pending shares that aren't already in the list
    toAdd.forEach((shareToAdd) => {
      if (
        !loadedShares.some(
          (object) =>
            object.id === shareToAdd.id &&
            object.shareType === shareToAdd.shareType,
        )
      ) {
        loadedShares.push(shareToAdd as ConnectionShare);
      }
    });
    // Remove all pending deletes from the list
    return loadedShares.filter(
      (sourceObject) =>
        !toDelete.some(
          (filterObject) =>
            String(filterObject.id) === String(sourceObject.id) &&
            filterObject.shareType === sourceObject.shareType,
        ),
    );
  }, [shares, toDelete, toAdd]);

  // Checked shares are added; unchecked shares are removed. Owner is not toggleable.
  const onToggleShare = (id: Key, shareType: ConnectionShareType) => {
    if (shareType === ConnectionShareType.Owner) return;
    if (findShare(id, shareType, sharesToDisplay)) {
      onRemove(id, shareType);
    } else {
      onAdd(id, shareType);
    }
  };

  return {
    onAdd,
    onRemove,
    onToggleShare,
    toAdd: uniqBy(toAdd, shareKey), // Filter out all duplicates if any
    toDelete: uniqBy(toDelete, shareKey), // Filter out all duplicates if any
    shares: sharesToDisplay,
    isSharesLoading: isLoading,
  };
}
